import jsPDF from "jspdf";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";
import dayjs from "dayjs";

// ─────────────────────────────────────────────────────────────────────────────
// เทียบชื่อโจทก์จาก lawsuit.COMPANY_ID  ★ แก้ชื่อบริษัทให้ตรงระบบจริง ★
// ─────────────────────────────────────────────────────────────────────────────
const COMPANY_NAME = {
  1: "บริษัท วัน ลิสซิ่ง จำกัด",
  2: "บริษัท วัน มันนี่ จำกัด",
  3: "บริษัท วัน อุดร จำกัด",
  4: "บริษัท วัน ชลบุรี จำกัด",
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const THAI_MONTHS = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

// แปลงวันที่เป็นไทย เช่น 9 มีนาคม 2569
const fmtThaiDate = (v) => {
  if (!v) return "-";
  const d = dayjs(v);
  if (!d.isValid()) return "-";
  return `${d.date()} ${THAI_MONTHS[d.month()]} ${d.year() + 543}`;
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  return Number(v).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// interest_rate เก็บเป็นสัดส่วน (0.05 = ร้อยละ 5)
const fmtRate = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  return `ร้อยละ ${(Number(v) * 100).toLocaleString("th-TH")} ต่อปี`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────────────────
export const ExportJudgementCoverPDF = (data = {}, record = {}) => {
  const lawsuit = data?.lawsuit ?? {};
  const judge = data?.judge ?? {};
  const agreement = data?.agreement ?? null;

  // ทำยอม = มีข้อมูล agreement / ไม่มี = พิพากษาปกติ
  const isSettlement = !!agreement?.id;

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const PW = 210;
  const ML = 15;
  const MR = 15;
  const TW = PW - ML - MR;

  const ROW_H = 8;
  const LABEL_W = 55;
  const VALUE_W = TW - LABEL_W;

  let y = 15;

  // ── กรอบ cell ──────────────────────────────────────────────────────────
  const drawCell = (x, yy, w, h, text, align = "left", opts = {}) => {
    const {
      fillColor = null,
      fontSize = 14,
      bold = false,
      paddingX = 3,
      textColor = [0, 0, 0],
    } = opts;

    if (fillColor) {
      doc.setFillColor(...fillColor);
      doc.rect(x, yy, w, h, "F");
    }
    doc.setLineWidth(0.3);
    doc.setDrawColor(0, 0, 0);
    doc.rect(x, yy, w, h, "S");

    doc.setFont("THSarabunNew", bold ? "bold" : "normal");
    doc.setFontSize(fontSize);
    doc.setTextColor(...textColor);

    const xText =
      align === "right"
        ? x + w - paddingX
        : align === "center"
          ? x + w / 2
          : x + paddingX;

    doc.text(String(text ?? "-"), xText, yy + h * 0.65, { align });
    doc.setTextColor(0, 0, 0);
  };

  // ── แถว label : value ──────────────────────────────────────────────────
  const drawRow = (label, value, opts = {}) => {
    drawCell(ML, y, LABEL_W, ROW_H, label, "left", {
      fillColor: [235, 240, 250],
      bold: true,
      ...opts.labelOpts,
    });
    drawCell(ML + LABEL_W, y, VALUE_W, ROW_H, value, "left", {
      fillColor: [255, 255, 255],
      ...opts.valueOpts,
    });
    y += ROW_H;
  };

  // ── หัวข้อ section ─────────────────────────────────────────────────────
  const drawSectionHeader = (title) => {
    drawCell(ML, y, TW, ROW_H + 1, title, "center", {
      fillColor: [200, 220, 255],
      bold: true,
      fontSize: 15,
    });
    y += ROW_H + 1;
  };

  // ═════════════════════════ HEADER ══════════════════════════════════════
  doc.setFont("THSarabunNew", "bold");
  doc.setFontSize(18);
  doc.text("ใบปะหน้าคำพิพากษา", PW / 2, y, { align: "center" });

  doc.setFont("THSarabunNew", "normal");
  doc.setFontSize(11);
  doc.setTextColor(150, 150, 150);
  doc.text(`วันที่พิมพ์ ${dayjs().format("DD/MM/YYYY")}`, PW - MR, 10, {
    align: "right",
  });
  doc.setTextColor(0, 0, 0);
  doc.text(`ผู้รับผิดชอบ ${record.LAWYER_NNAME}`, PW - MR, 15, {
    align: "right",
  });

  y += 6;
  doc.setFont("THSarabunNew", "bold");
  doc.setFontSize(14);
  doc.text(`เลขที่สัญญา ${lawsuit.CONTNO || "-"}`, PW / 2, y, {
    align: "center",
  });
  y += 7;

  // ═════════════════════ ข้อมูลคำฟ้อง ═════════════════════════════════════
  drawSectionHeader("ข้อมูลคำฟ้อง");

  const defendantMain = `${lawsuit.customer_title || ""}${
    lawsuit.customer_name || ""
  } ${lawsuit.customer_lastname || ""}`.trim();

  const sumLawsuitFee =
    Number(lawsuit.fee || 0) +
    Number(lawsuit.delivery_of_summons || 0) +
    Number(lawsuit.document_cost || 0);

  drawRow("โจทก์", COMPANY_NAME[lawsuit.COMPANY_ID] ?? "-");
  drawRow("จำเลย", defendantMain || "-");
  drawRow("เรื่อง", lawsuit.subject || "-");
  drawRow("วันประทับฟ้อง", fmtThaiDate(lawsuit.date_of_plaint));
  drawRow("เลขคดีดำ", lawsuit.black_case_number || "-");
  drawRow("วันนัดศาล", fmtThaiDate(lawsuit.consideration_date));
  drawRow("ศาลจังหวัด", lawsuit.provincial_court || "-");
  drawRow("ทุนทรัพย์ใช้ฟ้อง", `${fmtMoney(lawsuit.litigation_funds)} บาท`);
  drawRow("ค่าธรรมเนียมศาล", `${fmtMoney(lawsuit.fee)} บาท`);
  drawRow("ค่าส่งหมาย", `${fmtMoney(lawsuit.delivery_of_summons)} บาท`);
  drawRow("ค่าจัดทำเอกสาร", `${fmtMoney(lawsuit.document_cost)} บาท`);
  drawRow("รวมค่าใช้จ่าย", `${fmtMoney(sumLawsuitFee)} บาท`, {
    labelOpts: { fillColor: [210, 225, 255] },
    valueOpts: { bold: true },
  });

  y += 4;

  // ═════════════════════ ข้อมูลพิพากษา ════════════════════════════════════
  drawSectionHeader(
    isSettlement ? "ข้อมูลพิพากษา (ทำยอม/ประนีประนอม)" : "ข้อมูลพิพากษา",
  );

  drawRow("เลขคดีแดง", judge.red_case_number || "-");
  drawRow("วันที่พิพากษา", fmtThaiDate(judge.judge_date));
  drawRow("ค่าใช้แทน", `${fmtMoney(judge.judgement)} บาท`);
  drawRow("ต้นเงิน", `${fmtMoney(judge.judgement_lack)} บาท`);

  if (isSettlement) {
    // ── กรณีทำยอม ──
    drawRow("ผ่อนชำระเดือนละ", `${fmtMoney(agreement.installment_amount)} บาท`);
    drawRow(
      "จำนวน",
      agreement.installment_count != null
        ? `${agreement.installment_count} งวด`
        : "-",
    );
    drawRow("วันที่ชำระงวดแรก", fmtThaiDate(agreement.due_date));
  } else {
    // ── กรณีพิพากษาปกติ ──
    // ค่าขาดประโยชน์รายเดือน/จำนวนเดือน ใช้ค่าจาก defendants คนแรก
    const firstDef = judge.defendants?.[0] ?? {};

    drawRow("ดอกเบี้ยต้นเงิน", fmtRate(judge.interest_rate));
    drawRow("ค่าขาดประโยชน์", `${fmtMoney(judge.lack_of_benefits)} บาท`);
    drawRow("ดอกเบี้ยค่าขาดประโยชน์", fmtRate(judge.interest_rate_of_lack));
    drawRow(
      "ค่าขาดประโยชน์รายเดือน",
      `${fmtMoney(firstDef.cost_of_useleseness_per_month)} บาท`,
    );
    drawRow(
      "จำนวน",
      firstDef.cost_of_useleseness_month != null
        ? `${firstDef.cost_of_useleseness_month} เดือน`
        : "-",
    );
  }

  drawRow("ค่าทนาย", `${fmtMoney(judge.attorney_fees)} บาท`);
  drawRow("ค่าติดตาม", `${fmtMoney(judge.tracking_fee)} บาท`);

  y += 4;

  // ═════════════════════ รายชื่อจำเลย ══════════════════════════════════════
  drawSectionHeader("รายชื่อจำเลย");

  // ข้อมูล defendants อาจมีซ้ำ (คนเดียวกันหลาย judge_number)
  // → dedupe ด้วย defendant_number แล้วเรียงตามลำดับจำเลย
  const defendants = [
    ...new Map(
      (judge.defendants ?? []).map((d) => [d.defendant_number, d]),
    ).values(),
  ].sort((a, b) => (a.defendant_number ?? 0) - (b.defendant_number ?? 0));

  const defCols = [
    { label: "จำเลยที่", w: 30, align: "center" },
    { label: "ชื่อ-นามสกุล", w: TW - 30, align: "left" },
  ];

  // header ตาราง
  let x = ML;
  defCols.forEach((col) => {
    drawCell(x, y, col.w, ROW_H, col.label, "center", {
      fillColor: [210, 210, 210],
      bold: true,
      fontSize: 13,
    });
    x += col.w;
  });
  y += ROW_H;

  if (defendants.length === 0) {
    drawCell(ML, y, TW, ROW_H, "— ไม่มีข้อมูลจำเลย —", "center", {
      fillColor: [255, 255, 255],
    });
    y += ROW_H;
  } else {
    defendants.forEach((d, idx) => {
      const fullName =
        `${d.SNAM || ""}${d.NAME1 || ""} ${d.NAME2 || ""}`.trim();
      const fill = idx % 2 === 0 ? [255, 255, 255] : [245, 248, 255];

      drawCell(
        ML,
        y,
        defCols[0].w,
        ROW_H,
        d.defendant_number ?? "-",
        "center",
        {
          fillColor: fill,
        },
      );
      drawCell(ML + defCols[0].w, y, defCols[1].w, ROW_H, fullName, "left", {
        fillColor: fill,
      });
      y += ROW_H;
    });
  }

  // ═════════════════════ PRINT ════════════════════════════════════════════
  doc.autoPrint();
  const blobUrl = doc.output("bloburl");
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = blobUrl;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    iframe.contentWindow.print();
    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(blobUrl);
    }, 1000);
  };
};
